/**
 * AI API 호출용 공용 유틸리티
 * - fetchWithRetry: 타임아웃 + Exponential Backoff 자동 재시도
 * - calcApiTimeout: 작업 유형별 동적 타임아웃 계산
 */

// 단일 AI 호출은 백엔드 Gemini SDK timeout(600s) 안쪽으로 유지한다.
// 이미지 일괄 채점은 여러 Vision 호출을 순차/병렬로 나눠 수행하므로 요청 전체는 더 오래 걸릴 수 있다.
import { reportApiNetworkError } from './clientErrorReporter';

const BACKEND_SINGLE_CALL_LIMIT_MS = 590_000;
const BATCH_IMAGE_REQUEST_LIMIT_MS = 1_800_000; // 30분

/**
 * 작업 유형별 동적 타임아웃 계산 (밀리초)
 *
 * @param {'batch-scoring'|'student-split'|'batch-saenggibu'|'saenggibu-generate'|'saenggibu-file-analysis'|'batch-inspector'|'single-inspector'|'assessment-plan'|'saenggibu-refine'|'evaluation-elements-suggest'|'inspector-refine'|'scoring-refine'|'single-scoring'|'default'} type
 * @param {object} params - { fileCount, hasImages, studentCount, pageCount }
 * @returns {number} 타임아웃 밀리초
 */
export function calcApiTimeout(type, params = {}) {
  const { fileCount = 1, hasImages = false, studentCount = 1, pageCount = 1 } = params;

  switch (type) {
    case 'batch-scoring': {
      // 2026-06: Vision 동시성을 안정성 우선(기본 2)으로 낮추면서 이미지 묶음은 더 오래 기다린다.
      const base = 180_000;
      const perFile = hasImages ? 90_000 : 8_000;
      const aiScoring = Math.min(fileCount * 3_000, 120_000);
      const buffer = hasImages ? 90_000 : 60_000;
      const raw = base + (perFile * fileCount) + aiScoring + buffer;
      return Math.min(raw, hasImages ? BATCH_IMAGE_REQUEST_LIMIT_MS : BACKEND_SINGLE_CALL_LIMIT_MS);
    }
    case 'scan-pdf-scoring': {
      // 학생별 페이지 범위 Vision 호출도 안정성 우선 동시성(기본 2)에 맞춰 넉넉하게 기다린다.
      // pageCount는 PDF 전체 페이지 수, studentCount는 실제 Vision 채점 호출 수에 가깝다.
      const base = 120_000;
      const units = Math.max(1, Number(studentCount) || Number(pageCount) || 1);
      const perUnit = 60_000;
      const pageReadBuffer = Math.min(Math.max(1, Number(pageCount) || 1) * 1_000, 90_000);
      const aiScoring = Math.min(units * 3_000, 120_000);
      const buffer = 90_000;
      const raw = base + (perUnit * units) + pageReadBuffer + aiScoring + buffer;
      return Math.min(raw, BATCH_IMAGE_REQUEST_LIMIT_MS);
    }
    case 'student-split': {
      // 파일 텍스트 추출 뒤 AI가 학생 경계를 판단한다. Office/HWP 변환과 Gemini JSON 응답까지 고려한다.
      return BACKEND_SINGLE_CALL_LIMIT_MS;
    }
    case 'batch-saenggibu': {
      // 교과세특 다중 생성은 학생 제출물 기반 청크 생성 + 일부 개별 보강 호출이 있어
      // 80명 학급 파일도 batch-scoring 이미지 흐름처럼 더 오래 기다릴 수 있어야 한다.
      const base = 180_000;
      const perStudent = 20_000;
      const buffer = 120_000;
      return Math.min(base + (perStudent * studentCount) + buffer, BATCH_IMAGE_REQUEST_LIMIT_MS);
    }
    case 'saenggibu-generate': {
      // 단일 생기부도 교과 30명/파일 첨부/JSON repair 시 한 호출이 길어질 수 있다.
      // StreamingResponse는 첫 progress 이벤트로 연결을 열고, 이후 inactivity timeout이 별도로 감시한다.
      const base = 120_000;
      const perStudent = 12_000;
      const perFile = 20_000;
      const buffer = 90_000;
      return Math.min(base + (perStudent * studentCount) + (perFile * fileCount) + buffer, BACKEND_SINGLE_CALL_LIMIT_MS);
    }
    case 'saenggibu-file-analysis': {
      // 로컬 파싱/문서 OCR 뒤 Gemini 구조화 분석이 순차 실행된다.
      // SDK 단일 timeout(600초)보다 바깥 fetch timeout을 길게 유지한다.
      const base = hasImages ? 1_200_000 : 720_000;
      const additionalFile = hasImages ? 200_000 : 60_000;
      return Math.min(
        base + (Math.max(1, fileCount) - 1) * additionalFile,
        BATCH_IMAGE_REQUEST_LIMIT_MS
      );
    }
    case 'batch-inspector': {
      // 배치 검사는 기재요령 원문 + 학생 전체를 단일 Gemini 호출로 깊게 검사한다.
      // 학생 수가 적어도 thinking budget이 커서 2~5분 이상 걸릴 수 있으므로
      // 백엔드 단일 호출 한도 바로 안쪽까지 넉넉하게 기다린다.
      const base = 180_000;
      const perItem = 20_000;
      const buffer = 120_000;
      return Math.min(base + (perItem * studentCount) + buffer, BACKEND_SINGLE_CALL_LIMIT_MS);
    }
    case 'single-inspector':
      // iOS WebKit은 약 60초에 fetch를 끊으므로 그 전에 제어 가능한 안내를 보여준다.
      return 55_000;
    case 'assessment-plan':
      return 300_000; // 5분
    case 'similar-sentences': {
      const units = Math.max(1, Number(studentCount) || 1);
      return Math.min(180_000 + (units * 6_000) + 90_000, BATCH_IMAGE_REQUEST_LIMIT_MS);
    }
    case 'saenggibu-refine':
      return 180_000; // 3분
    case 'evaluation-elements-suggest':
      // 성취기준 → 평가요소 예시 3개(JSON). flash-lite low thinking 실측 3.3s(2026-09).
      // 서버는 Gemini SDK 40s, 전체 75s로 끊으므로 바깥 fetch는 그보다 길게 90s.
      return 90_000;
    case 'inspector-refine':
      return 180_000; // 3분
    case 'scoring-refine':
      return 180_000; // 3분
    case 'single-scoring':
      return 180_000; // 3분
    case 'rubric-extract':
      return 120_000; // 2분
    case 'voice-transcribe':
      return 120_000; // 2분 녹음 받아쓰기 실측 ~10초, 서버 SDK 45초 x 2회보다 길게
    case 'voice-speak':
      return 150_000; // 첫 소리까지만 잰다(실측 ~1.5초). 서버 첫 소리 대기 60초 x 2회보다 길게
    default:
      return 300_000; // 5분 기본값
  }
}

/**
 * 재시도 가능한 fetch wrapper (Exponential Backoff)
 *
 * @param {string} url
 * @param {RequestInit} options
 * @param {object} config
 * @param {number} config.maxRetries - 최대 재시도 횟수 (기본 2)
 * @param {number} config.timeoutMs - 요청 타임아웃 밀리초 (기본 300,000)
 * @param {function} config.onRetry - 재시도 시 콜백 (attempt, maxRetries)
 * @param {boolean} config.retryOnTimeout - 타임아웃(AbortError) 시 재시도할지 여부 (기본 true).
 *   대용량 작업(일괄 채점 등)에서 같은 거대한 호출을 반복해 봐야 동일한 결과가 나오므로
 *   false로 설정하여 즉시 사용자에게 알리는 게 낫다.
 * @returns {Promise<Response>}
 */
export async function fetchWithRetry(url, options = {}, {
  maxRetries = 2,
  timeoutMs = 300_000,
  onRetry,
  retryOnTimeout = true,
} = {}) {
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    const controller = new AbortController();
    if (options.signal) {
      if (options.signal.aborted) {
        controller.abort();
      } else {
        options.signal.addEventListener('abort', () => controller.abort(), { once: true });
      }
    }
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    try {
      const response = await fetch(url, {
        ...options,
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      if (response.status >= 500 && attempt < maxRetries) {
        const delay = Math.min(3000 * Math.pow(2, attempt), 15000);
        onRetry?.(attempt + 1, maxRetries);
        await new Promise((r) => setTimeout(r, delay));
        continue;
      }
      return response;
    } catch (error) {
      clearTimeout(timeoutId);
      const isAbort = error.name === 'AbortError';
      if (isAbort && options.signal?.aborted) {
        const cancelledError = new Error('요청을 취소했어요.');
        cancelledError.name = 'AbortError';
        throw cancelledError;
      }
      const isNetwork =
        error.message?.includes('Failed to fetch') ||
        error.message?.includes('NetworkError') ||
        error.message?.includes('Load failed');

      // 네트워크 일시 오류는 항상 재시도 (가벼운 호출이므로 효과 있음).
      // 타임아웃은 retryOnTimeout이 true일 때만 재시도.
      const shouldRetry = (isNetwork || (isAbort && retryOnTimeout)) && attempt < maxRetries;
      if (shouldRetry) {
        const delay = Math.min(3000 * Math.pow(2, attempt), 15000);
        onRetry?.(attempt + 1, maxRetries);
        await new Promise((r) => setTimeout(r, delay));
        continue;
      }
      // 재시도를 다 쓰고도 실패: 서버에는 흔적이 없는 실패(네트워크 단절·타임아웃)라 클라이언트가 직접 남긴다.
      reportApiNetworkError({
        attempts: attempt + 1,
        error,
        kind: isAbort ? 'timeout' : (isNetwork ? 'network' : 'fetch-error'),
        method: options.method,
        url,
      });
      if (isAbort) {
        throw new Error('서버 응답 시간이 초과되었어요. 잠시 후 다시 시도해주세요.');
      }
      throw error;
    }
  }
}
